import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { format } from 'date-fns';

import { ApplicationConfig } from '../../../../core/config/application.config';
import {
  Message,
  MessageCreatePayload,
} from '../../../../domain/message/model/message';
import { MessageThread } from '../../../../domain/message/model/message-thread';
import { MessageThreadCriteria } from '../../../../domain/message/model/message-thread-criteria';
import { Page } from '../../../../shared/model/local/pagination/page';
import { HttpUtil } from '../../../../shared/util/http.util';
import { ObjectUtil } from '../../../../shared/util/object.util';

/** Backend expects date-only YYYY-MM-DD, not ISO with time. */
function toDateOnly(value: string | Date): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  return format(d, 'yyyy-MM-dd');
}

function buildMessageFormData(
  message: MessageCreatePayload,
  files?: File[]
): FormData {
  const formData = new FormData();
  formData.append('dtoString', JSON.stringify({ message }));
  if (files?.length) {
    files.forEach(file => formData.append('files', file, file.name));
  }
  return formData;
}

@Injectable({ providedIn: 'root' })
export class MessageApiService {
  private readonly http = inject(HttpClient);
  private readonly httpUtil = inject(HttpUtil);

  getMessageThreadPage(
    criteria: MessageThreadCriteria
  ): Observable<Page<MessageThread>> {
    let params = new HttpParams()
      .set('pageNumber', criteria.pageNumber.toString())
      .set('pageSize', criteria.pageSize.toString());

    const backendSort = ObjectUtil.toBackendParams({
      sortOrders: criteria.sortOrders,
    });
    const sortPaths = backendSort['sortPaths'] as string[] | undefined;
    const sortDirections = backendSort['sortDirections'] as
      | string[]
      | undefined;
    if (sortPaths?.length && sortDirections?.length) {
      params = params.set('sortPaths', sortPaths.join(','));
      params = params.set('sortDirections', sortDirections.join(','));
    }

    if (criteria.searchPhrase) {
      params = params.set('searchPhrase', criteria.searchPhrase);
    }

    if (criteria.status) {
      params = params.set('status', criteria.status);
    }

    if (criteria.dateFrom) {
      params = params.set('dateFrom', toDateOnly(criteria.dateFrom));
    }

    if (criteria.dateTo) {
      params = params.set('dateTo', toDateOnly(criteria.dateTo));
    }

    return this.http.get<Page<MessageThread>>(
      `${ApplicationConfig.apiUrl}/threads/page`,
      { params }
    );
  }

  getMessageThread(caseId: string): Observable<MessageThread> {
    return this.http.get<MessageThread>(
      `${ApplicationConfig.apiUrl}/threads/${caseId}`
    );
  }

  createMessageThread(
    caseId: string,
    message: MessageCreatePayload,
    files?: File[],
    options?: { silentMode?: boolean }
  ): Observable<MessageThread> {
    const formData = buildMessageFormData(message, files);
    return this.httpUtil.post(
      `${ApplicationConfig.apiUrl}/threads/${caseId}`,
      formData,
      { silentMode: options?.silentMode ?? true }
    ) as Observable<MessageThread>;
  }

  createMessage(
    caseId: string,
    message: MessageCreatePayload,
    files?: File[],
    options?: { silentMode?: boolean }
  ): Observable<Message> {
    const formData = new FormData();
    formData.append('dtoString', JSON.stringify(message));
    if (files?.length) {
      files.forEach(file => formData.append('files', file, file.name));
    }
    return this.httpUtil.post(
      `${ApplicationConfig.apiUrl}/threads/${caseId}/message`,
      formData,
      { silentMode: options?.silentMode ?? true }
    ) as Observable<Message>;
  }

  archiveMessageThread(caseId: string): Observable<MessageThread> {
    return this.http.post<MessageThread>(
      `${ApplicationConfig.apiUrl}/threads/${caseId}/archive`,
      {}
    );
  }
}
